// client/src/components/indicadores/ComissoesDetalhePanel.js
// Painel de detalhamento (drill-down) aberto ao clicar num profissional na
// tabela de Comissões. Existem DUAS faixas de comissão possíveis (uma por
// unidade, genérica; outra específica por serviço) — o painel separa as
// duas: "Comissão da Unidade" como um resumo único (KPI), e "Comissão por
// Serviço" com quantidade/valor/% próprios de cada serviço, expansível pra
// ver as transações que compõem aquele total. Mesmo padrão visual de
// expandir/recolher já usado em UnidadeComissoesSection.js (chevron +
// estado local), sem precisar de um Modal novo.
import React, { useMemo, useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';

const ComissoesDetalhePanel = ({ profissionalNome, itens, resumo, isLoading, currency, locale }) => {
    const [servicoExpandido, setServicoExpandido] = useState(null);

    const transacoesPorServico = useMemo(() => {
        const mapa = new Map();
        (itens || []).forEach((item) => {
            if (item.modelo !== 'Servico' || item.origemComissao !== 'servico') return;
            const lista = mapa.get(item.produtoId) || [];
            lista.push(item);
            mapa.set(item.produtoId, lista);
        });
        return mapa;
    }, [itens]);

    if (isLoading) {
        return (
            <div className="panel-state-wrapper" style={{ minHeight: '120px' }}>
                <Loader2 size={20} className="animate-spin icon" />
                <p>Carregando detalhamento...</p>
            </div>
        );
    }

    if (!itens || itens.length === 0) {
        return (
            <div className="panel-state-wrapper panel-info" style={{ minHeight: '80px' }}>
                <p>Nenhuma transação encontrada para este profissional no período/filtros selecionados.</p>
            </div>
        );
    }

    const porServico = resumo?.porServico || [];

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm bg-white">
            <div className="p-3 bg-gray-50 border-b border-gray-200">
                <span className="text-sm font-semibold text-gray-800">Detalhamento — {profissionalNome}</span>
            </div>

            <div className="flex flex-wrap gap-6 p-4 border-b border-gray-100">
                <div>
                    <div className="text-xs text-gray-500">Qtd. Total de Agendamentos</div>
                    <div className="text-lg font-bold text-gray-800">{resumo?.quantidadeTotalAgendamentos ?? 0}</div>
                </div>
                <div>
                    <div className="text-xs text-gray-500">Valor Total</div>
                    <div className="text-lg font-bold text-gray-800">{formatCurrency(resumo?.valorTotal ?? 0, currency, locale)}</div>
                </div>
                <div>
                    <div className="text-xs text-gray-500">Comissão da Unidade</div>
                    <div className="text-lg font-bold text-gray-800">{formatCurrency(resumo?.comissaoUnidade ?? 0, currency, locale)}</div>
                </div>
            </div>

            <div className="p-3 bg-gray-50 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-700">Comissão por Serviço</span>
            </div>

            {porServico.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">Nenhum serviço com comissão específica configurada neste período.</div>
            ) : (
                porServico.map((servico) => {
                    const aberto = servicoExpandido === servico.servicoId;
                    const transacoes = transacoesPorServico.get(servico.servicoId) || [];
                    return (
                        <div key={servico.servicoId} className="border-b border-gray-100 last:border-b-0">
                            <div
                                className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 group"
                                onClick={() => setServicoExpandido(aberto ? null : servico.servicoId)}
                            >
                                <div>
                                    <span className="text-sm font-medium text-gray-800">{servico.servicoNome}</span>
                                    <span className="text-xs text-gray-500 ml-2">{servico.quantidade} item(ns)</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs text-gray-500">
                                        {formatCurrency(servico.valorTotal, currency, locale)} · {formatCurrency(servico.comissaoTotal, currency, locale)} ({servico.percentualComissao}%)
                                    </span>
                                    <ChevronDown size={16} className={`text-gray-400 transition-transform ${aberto ? 'rotate-180' : ''}`} />
                                </div>
                            </div>

                            {aberto && (
                                <div className="table-wrapper pl-4 pb-2">
                                    <table className="styled-table">
                                        <thead>
                                            <tr>
                                                <th>Data</th>
                                                <th>Cliente</th>
                                                <th>Unidade</th>
                                                <th>Valor</th>
                                                <th>Comissão</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transacoes.map((t) => (
                                                <tr key={`${t.vendaId}-${t.itemId}`}>
                                                    <td>{new Date(t.data).toLocaleDateString('pt-BR')}</td>
                                                    <td>{t.clienteNome}</td>
                                                    <td>{t.unidadeNome}</td>
                                                    <td>{formatCurrency(t.valorItem, currency, locale)}</td>
                                                    <td>{formatCurrency(t.comissaoItem, currency, locale)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    );
                })
            )}
        </div>
    );
};

export default ComissoesDetalhePanel;
